import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const AssignResponsible = NiceModal.create(({ ids, mode }) => {
	const dispatch = useDispatch();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	const [isLoading, setIsLoading] = useState(false);

	const defaultColSpan = {
		md: 6
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					responsibleID: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			responsibleID: null,
			ids: ids.join(',')
		}
	});
	const { watch, setValue, handleSubmit } = formMethods;

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const onSubmit = async ({ responsibleID }) => {
		setIsLoading(true);

		const URL =
			mode === 'inbound'
				? ADVANCE_WMS_MODULE.ASSIGN_RESPONSIBLE_INBOUND
				: ADVANCE_WMS_MODULE.ASSIGN_RESPONSIBLE_OUTBOUND;

		try {
			await axiosPrivate.post(URL, {
				ids,
				responsibleID
			});
			dispatch(updateDynamicListsInPage());
			handleCloseModal();
			showSuccess();
		} catch (error) {
			showError();
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const responsibleAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);

	const getTitle = () => 'assignResponsible';
	return (
		<Modal fullwidth maxWidth="small">
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="IDs" isLoading={isLoading}>
						<StringInput name="ids" disabled />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="responsible" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSE_USERS}
							name="responsibleID"
							value={watch('responsibleID')}
							setValue={(value) => {
								setValue('responsibleID', value?.ID ?? null, {
									shouldDirty: true,
									shouldValidate: true
								});
							}}
							columns={responsibleAutocompleteColumns}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={'save'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AssignResponsible;
